import type { TranscriptBlock } from '../transport/api'

/**
 * A transcript, cut into what a reader actually scans for.
 *
 * The unit is a TURN: everything the agent did between one message TO it and the
 * next. Measured on real transcripts rather than guessed — in avengents-server's, one
 * turn holds 26 assistant messages and 55 tool calls, with a median of 7 messages per
 * turn. That is the agent narrating its way to an answer, and reading it top to bottom
 * means scrolling through the working to reach the conclusion.
 *
 * NOT adjacent-block grouping, which was the obvious reading of "collapse consecutive
 * output" and does nothing: directly consecutive assistant blocks essentially do not
 * occur. Counted across all three sources — the longest run of adjacent assistant
 * blocks in 941 blocks is TWO, and almost every run is one. They read as consecutive
 * on screen because tool runs sit between them, already collapsed to a line each.
 *
 * A `user` block is the boundary, and that includes a message that ARRIVED from
 * another agent — `parseInbound` renders those from user blocks too. Someone else
 * speaking ends the turn just as the owner speaking does, because the question "what
 * happened since the last thing said to it" is the same question either way.
 */

export type TurnItem =
  /** A message TO the agent — the owner's, or one that arrived. Never collapsed. */
  | { kind: 'said'; block: TranscriptBlock }
  /** Everything the agent did in reply, in order. */
  | { kind: 'turn'; blocks: TranscriptBlock[] }

export function groupTurns(blocks: TranscriptBlock[]): TurnItem[] {
  const out: TurnItem[] = []
  let run: TranscriptBlock[] = []
  const flush = () => {
    if (run.length) out.push({ kind: 'turn', blocks: run })
    run = []
  }
  for (const b of blocks) {
    if (b.kind === 'user') {
      flush()
      out.push({ kind: 'said', block: b })
    } else {
      run.push(b)
    }
  }
  flush()
  return out
}

/**
 * The message a folded turn is BUILT ON: the agent's LAST piece of prose. It carries the
 * head and the fold control; what the agent said before it is `turnProse`, drawn above
 * it or not as the reader's switch says (everyReply.ts).
 *
 * The last one rather than the first, because a turn ends with its conclusion — the
 * opening message is usually "I'll start by reading the file". Tool calls and thinking
 * are never the summary: they are the working, which is what collapsing is for.
 *
 * Null when the agent said nothing at all that turn — it ran commands and stopped.
 * That is a real state and the caller must render it as one, rather than showing the
 * last tool call as though the agent had said it.
 */
export function turnSummary(blocks: TranscriptBlock[]): TranscriptBlock | null {
  for (let i = blocks.length - 1; i >= 0; i--) {
    const b = blocks[i]!
    if (b.kind === 'assistant' && (b.text ?? '').trim()) return b
  }
  return null
}

/**
 * What the agent SAID in a run of blocks, in order — its prose and nothing else.
 *
 * The same test `turnSummary` applies to pick the last one: an assistant block with
 * words in it. Tool calls, their results and thinking are the working; a block that is
 * only whitespace is nothing said.
 */
export function turnProse(blocks: TranscriptBlock[]): TranscriptBlock[] {
  return blocks.filter((b) => b.kind === 'assistant' && (b.text ?? '').trim())
}

/**
 * Is this turn worth collapsing?
 *
 * A turn that is already one message is not: collapsing it would add a control, hide
 * nothing, and make a short exchange read as though something were concealed. The
 * threshold is what the turn contains, not how tall it renders — height depends on the
 * text and on the width of the rail, and a rule that changed as you dragged the
 * sidebar would be impossible to predict.
 */
export function worthCollapsing(blocks: TranscriptBlock[]): boolean {
  return blocks.length > 1
}
